import { useTranslation } from "react-i18next";
import {
  useCVData,
  LAYOUT_OPTIONS,
  COLOR_OPTIONS,
} from "../../../lib/cv-mock-data";
import { MaterialSymbol } from "../../icons/MaterialSymbol";

const FIELD_LABEL_STYLE: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 500,
  color: "var(--text-secondary)",
  marginBottom: 12,
  display: "block",
  fontFamily: "inherit",
};

export function EditCustomizeForm() {
  const { t } = useTranslation("seeker");
  const {
    profile,
    cvConfig,
    updateCVConfig,
    setCVPhotoVisibility,
    setCVFitToOnePage,
  } = useCVData();

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      {/* Layout: segmented chips */}
      <div>
        <label style={FIELD_LABEL_STYLE}>
          {t("cv.edit.customizeLayout", { defaultValue: "Layout" })}
        </label>
        <div className="tabbio-cv-layout-options" style={{ display: "flex", gap: 8 }}>
          {LAYOUT_OPTIONS.map((layout) => {
            const isActive = cvConfig.style === layout.id;
            return (
              <button
                key={layout.id}
                type="button"
                onClick={() => updateCVConfig({ style: layout.id })}
                className={isActive ? "tabbio-chip active" : "tabbio-chip"}
                style={{ flex: 1, justifyContent: "center" }}
              >
                {layout.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Photo: circular toggle with avatar preview */}
      {profile.avatarUrl ? (
        <div>
          <label style={FIELD_LABEL_STYLE}>
            {t("cv.edit.customizePhoto", { defaultValue: "Photo" })}
          </label>
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              setCVPhotoVisibility(!cvConfig.showPhoto);
            }}
            aria-pressed={cvConfig.showPhoto}
            aria-label={
              cvConfig.showPhoto
                ? t("cv.edit.customizePhotoHide", {
                    defaultValue: "Hide photo",
                  })
                : t("cv.edit.customizePhotoShow", {
                    defaultValue: "Show photo",
                  })
            }
            style={{
              width: 48,
              height: 48,
              borderRadius: "50%",
              overflow: "hidden",
              position: "relative",
              border: cvConfig.showPhoto
                ? "2px solid var(--text-primary)"
                : "1px solid var(--gem-outline)",
              cursor: "pointer",
              padding: 0,
              background: "transparent",
              transition: "border-color 150ms",
              flexShrink: 0,
            }}
          >
            <img
              src={profile.avatarUrl}
              alt=""
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                opacity: cvConfig.showPhoto ? 1 : 0.4,
                filter: cvConfig.showPhoto ? "none" : "grayscale(100%)",
              }}
            />
            {!cvConfig.showPhoto ? (
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "rgba(0,0,0,0.35)",
                  color: "#fff",
                }}
              >
                <MaterialSymbol name="visibility_off" size={20} />
              </div>
            ) : null}
          </button>
        </div>
      ) : null}

      {/* Fit to one page: best-effort single-page mode, off by default. */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 16,
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 14,
              fontWeight: 500,
              color: "var(--text-primary)",
              fontFamily: "inherit",
            }}
          >
            {t("cv.edit.customizeFitOnePage", {
              defaultValue: "Keep PDF to one page",
            })}
          </div>
          <div
            style={{
              fontSize: 12.5,
              lineHeight: 1.4,
              color: "var(--text-tertiary)",
              marginTop: 2,
              fontFamily: "inherit",
            }}
          >
            {cvConfig.fitToOnePage === true
              ? t("cv.edit.customizeFitOnePageEnabledHint", {
                  defaultValue:
                    "One-page mode is on. CVs that already fit will look unchanged",
                })
              : t("cv.edit.customizeFitOnePageHint", {
                  defaultValue:
                    "Uses a compact layout only when the exported PDF needs it",
                })}
          </div>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={cvConfig.fitToOnePage === true}
          aria-label={t("cv.edit.customizeFitOnePage", {
            defaultValue: "Keep PDF to one page",
          })}
          onClick={(event) => {
            event.stopPropagation();
            setCVFitToOnePage(cvConfig.fitToOnePage !== true);
          }}
          style={{
            position: "relative",
            width: 58,
            height: 34,
            borderRadius: 9999,
            border: "1px solid var(--input-border)",
            cursor: "pointer",
            flexShrink: 0,
            padding: 0,
            // Same theme-var contract as the share sheet's toggle (issue #226):
            // ON = text-primary track + bg knob, OFF = input-bg track.
            background:
              cvConfig.fitToOnePage === true
                ? "var(--text-primary)"
                : "var(--input-bg)",
            boxShadow: "var(--gem-input-shadow)",
            transition: "background-color 150ms",
          }}
        >
          <span
            style={{
              position: "absolute",
              top: 4,
              insetInlineStart: cvConfig.fitToOnePage === true ? 28 : 4,
              width: 24,
              height: 24,
              borderRadius: 12,
              background:
                cvConfig.fitToOnePage === true
                  ? "var(--bg)"
                  : "var(--text-tertiary)",
              transition: "inset-inline-start 150ms, background-color 150ms",
            }}
          />
        </button>
      </div>

      {/* Color: row of colored dots. Colors are information (the user's
         accent choice), so colored swatches are allowed per §1. */}
      <div>
        <label style={FIELD_LABEL_STYLE}>
          {t("cv.edit.customizeColor", { defaultValue: "Color" })}
        </label>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          {COLOR_OPTIONS.map((color) => {
            const isActive = cvConfig.color === color.id;
            return (
              <button
                key={color.id}
                type="button"
                onClick={() => updateCVConfig({ color: color.id })}
                aria-label={color.id}
                aria-pressed={isActive}
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  border: "none",
                  outline: isActive
                    ? "2px solid var(--text-primary)"
                    : "1px solid var(--gem-outline)",
                  outlineOffset: isActive ? 2 : 0,
                  cursor: "pointer",
                  backgroundColor: color.hex,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: 0,
                  transition: "outline-color 150ms, outline-offset 150ms",
                }}
              >
                {isActive ? (
                  <MaterialSymbol
                    name="check"
                    size={16}
                    style={{ color: "#fff" }}
                  />
                ) : null}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
